import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { UtilsService } from 'core/utils/utils.service';
import { LastQuotesFilters } from 'src/app/modules/marketing/products/entities/last-quotes-filters.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { ConfidenceToClose, ConfidenceToCloseNames } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { countriesWithNoCurrencyDisplayed } from 'core/app-info/country-specifics.enum';
import { Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'app-quotes-search',
  templateUrl: './quotes-search.component.html',
  styleUrls: ['./quotes-search.component.scss'],
  standalone: false
})
export class QuotesSearchComponent implements OnInit, OnDestroy {
  appStrings: AppLocalization;
  dsDateFormat = this.appInfoService.dsDateFormat;
  isAccountManager = false;
  isMobile = false;
  accountManagerId: string;
  maxCharSearchDisplay = 30;
  quotesSearchForm: FormGroup<any>;
  isSelectState = false;
  templateValuesState: any[];
  cachedSearchValues: LastQuotesFilters;
  buttons = [
    { label: 'Clear all filters', type: 'tertiary', disableEmpty: true },
    { label: 'Search', type: 'primary' },
  ];
  dialogData: any;
  loading = true;
  disableButtons = false;

  displayedTextAmount: string;
  displayedTextAmountFrom: string;
  displayedTextAmountTo: string;
  countryCode = '';
  currency = '';
  subs = new Subscription();

  confidenceToCloseDisplay: { value: string, label: string }[];

  constructor(
    public dialogRef: MatDialogRef<QuotesSearchComponent>,
    private appInfoService: AppInfoService,
    private utilsService: UtilsService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.dialogData = data;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.confidenceToCloseDisplay = this.utilsService.mapKeyValueOptions(ConfidenceToCloseNames);
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters + StorageValues.LastTenQuotesKey));
    this.quotesSearchForm = this.fb.group({
      startDate: [this.cachedSearchValues?.startDate],
      endDate: [this.cachedSearchValues?.endDate],
      customerName: [this.cachedSearchValues?.customerName],
      bpNumber: [this.cachedSearchValues?.bpNumber],
      quoteName: [this.cachedSearchValues?.quoteName],
      quoteNumber: [this.cachedSearchValues?.quoteNumber],
      confidence: [ConfidenceToClose[this.cachedSearchValues?.confidence]],
      dollarAmount: [this.cachedSearchValues?.dollarAmount],
      expiringInDays: [this.cachedSearchValues?.expiringInDays],
      dollarRangeMin: [this.cachedSearchValues?.dollarRangeMin],
      dollarRangeMax: [this.cachedSearchValues?.dollarRangeMax],
      displayedSearchValue: [null]
    });

    this.disableButtons = !this.utilsService.formHasAnyValue(this.quotesSearchForm);

    this.subs.add(this.quotesSearchForm.valueChanges.subscribe(() => {
      this.disableButtons = !this.utilsService.formHasAnyValue(this.quotesSearchForm);
    }));

    this.countryCode = this.appInfoService.userData?.UserInfo?.CountryCode || 'US';
    this.determineAmountTextToDisplay();

    this.loading = false;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }


  btnCallback(i: number) {
    if (i === 0) {
      this.quotesSearchForm.reset();
    } else {
      const displayValue = this.formatDisplayValue();
      this.quotesSearchForm.controls.displayedSearchValue.setValue(displayValue);
      this.quotesSearchForm.controls.confidence.setValue(ConfidenceToClose[this.quotesSearchForm.controls.confidence.value]);
      sessionStorage.setItem(StorageValues.CachedFilters + StorageValues.LastTenQuotesKey, JSON.stringify(this.quotesSearchForm.value));
      this.dialogRef.close({ performSearch: true, searchDisplay: displayValue });
    }
  }

  formatDisplayValue() {
    let displayValue = '';
    Object.keys(this.quotesSearchForm.value).forEach((item) => {
      const value = this.quotesSearchForm.value[item];
      if (value && value !== '') {
        if (item === 'confidence') {
          displayValue += ConfidenceToCloseNames[value] + ';';
        } else if (item === 'startDate' || item === 'endDate') {
          displayValue += this.datePipe.transform(value, this.appInfoService.dateFormat) + ';';
        } else if (item !== 'displayedSearchValue') {
          displayValue += value + ';';
        }
      }
    });
    if (!displayValue.length) {
      return this.appStrings.accounts_search;
    }
    return displayValue.length > this.maxCharSearchDisplay ? displayValue.slice(0, this.maxCharSearchDisplay) + '...' : displayValue;
  }

  determineAmountTextToDisplay() {
    if (countriesWithNoCurrencyDisplayed[this.countryCode]) {
      this.displayedTextAmount = this.appStrings.quoteSearch_amount;
      this.displayedTextAmountFrom = this.appStrings.quoteSearch_rangesFrom;
      this.displayedTextAmountTo = this.appStrings.quoteSearch_rangesTo;
      this.currency = '';
    } else {
      this.displayedTextAmount = this.appStrings.quoteSearch_dollarAmount;
      this.displayedTextAmountFrom = this.appStrings.quoteSearch_dollarRangeFrom;
      this.displayedTextAmountTo = this.appStrings.quoteSearch_dollarRangeTo;
      this.currency = '$';
    }
  }
}
